import React, { useCallback, useEffect, useMemo, useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Typography, CircularProgress } from '@mui/material';
import Dialog from '../../../../components/Dialog/Dialog';
import AircraftDataGrid from './components/AircraftDataGrid';
import CalculationsSummaryAircraft from './components/CalculationsSummaryAircraft';
import ReferenceTablesInfo from './components/ReferenceTablesInfo';
import {
    EsalCalculationModalAirportProps,
    AircraftData,
    SummaryCalculationData,
    DEFAULT_AIRCRAFT_CONFIGURATIONS,
    DEFAULT_AIRCRAFT_VALUES,
    ESAL_CONSTANTS,
    CalculationsSummaryInputsAircraft,
} from './types/ESALTypesAircraft';
import { ESALFormulas } from './ESALFormulas';
import { useInvalidDecimalKeys } from '../../../hooks/useInvalidDecimalKeys';
import { useAppDispatch, useAppSelector } from '../../../../reduxService/hooks';
import { selectEsalAircraft, selectEsalAircraftSummaryInputs, setAircraft, updateAircraft, setSummaryInputsAircraft, resetEsalAircraftState } from '../../store/project/esalSliceAircraft';
import { selectDynamicProject } from '../../store/project/projectslice';

/**
 * Modal component for comprehensive Aircraft ESAL calculation.
 *
 * Features:
 * - Support for 8 aircraft types with real-time ESAL calculations
 * - Single table showing both directions for each aircraft
 * - Inline editing with Material-UI Table
 * - Summary calculation panel with traffic analysis
 * - Excel-based formula calculations (EALF, growth factors, etc.)
 */
const EsalCalculationModalAirport: React.FC<EsalCalculationModalAirportProps> = ({ open, onClose, onCalculationComplete }) => {
    const { t } = useTranslation();

    const dispatch = useAppDispatch();
    const aircraft = useAppSelector(selectEsalAircraft);
    const summaryInputs = useAppSelector(selectEsalAircraftSummaryInputs);
    const projectData = useAppSelector(selectDynamicProject);
    const [isCalculating, setIsCalculating] = useState<boolean>(false);
    const hasInitialized = useRef(false);
    const { hasInvalidKeys: hasInvalidDecimals, onValidityChange: onDecimalValidityChange, resetKey: decimalResetKey, reset: resetInvalidDecimals } = useInvalidDecimalKeys();

    // The cells are remounted on reopen: start again from a clean set
    const [seenOpen, setSeenOpen] = useState(open);
    if (open !== seenOpen) {
        setSeenOpen(open);
        if (open) resetInvalidDecimals();
    }
    const aircraftRef = useRef<AircraftData[]>(aircraft);

    /**
     * Inject global styles for this modal only
     */
    useEffect(() => {
        const styleId = 'esal-modal-airport-styles';

        if (open) {
            // Remove existing style if any
            const existingStyle = document.getElementById(styleId);
            if (existingStyle) {
                document.head.removeChild(existingStyle);
            }

            // Add new style targeting our specific dialog class
            const style = document.createElement('style');
            style.id = styleId;
            style.innerHTML = `
                .esal-calculation-modal-airport .MuiDialog-paper {
                    width: 2500px !important;
                    max-width: 98vw !important;
                    margin: 16px !important;
                }
                .esal-calculation-modal-airport .MuiDialog-container {
                    align-items: flex-start !important;
                }
            `;
            document.head.appendChild(style);
        }

        // Cleanup when modal closes or component unmounts
        return () => {
            const style = document.getElementById(styleId);
            if (style) {
                document.head.removeChild(style);
            }
        };
    }, [open]);

    /**
     * Calculate aircraft ESAL using total movements from both directions
     */
    const calculateAircraftESAL = useCallback((aircraft: AircraftData): AircraftData => {
        // Sum movements from both runways (same pattern as trucks)
        const totalMovements = (aircraft.runway1Vehicles || 0) + (aircraft.runway2Vehicles || 0);

        // FAA Excel pattern: E = IF(C14>0,C14,C13) - use user's load if >0, else design load
        const effectiveTotalLoad = aircraft.usersTotalLoad > 0 ? aircraft.usersTotalLoad : aircraft.designTotalLoad;

        if (!aircraft.selected || totalMovements <= 0) {
            // Still validate even when not calculating ESAL
            const config = DEFAULT_AIRCRAFT_CONFIGURATIONS.find(c => c.id === aircraft.id);
            const actualAxleCount = config ? config.steeringAxles + config.singleAxles + config.tandemAxles + config.tridemAxles : 0;
            const axleCountValid = config ? actualAxleCount === config.axles : false;
            const totalLoadPercentage = aircraft.steeringLoadPercentage + aircraft.singleLoadPercentage + aircraft.tandemLoadPercentage + aircraft.tridemLoadPercentage;
            const loadPercentagesValid = Math.abs(totalLoadPercentage - 100) < 0.01;

            return {
                ...aircraft,
                effectiveTotalLoad,
                ealfSteeringAxle: 0,
                ealfSingleAxle: 0,
                ealfTandemAxle: 0,
                ealfTridemAxle: 0,
                equivalentEalfPerVehicle: 0,
                esalRunway1: 0,
                esalRunway2: 0,
                esalPerDay: 0,
                passagePercentage: totalMovements > 0 ? 100 : 0,
                axleCountValid,
                loadPercentagesValid,
            };
        }

        // Get aircraft configuration from default configurations
        const config = DEFAULT_AIRCRAFT_CONFIGURATIONS.find(c => c.id === aircraft.id);
        if (!config) {
            console.error(`Configuration not found for aircraft ${aircraft.id}`);
            return { ...aircraft, effectiveTotalLoad, esalPerDay: 0 };
        }

        const structuralNumber = ESALFormulas.CONSTANTS.STRUCTURAL_NUMBER;

        // Convert constants (extracted from ESALFormulas.EXCEL_CONSTANTS)
        const KN_TO_KIPS = 0.224809;
        const GT = -0.20091484278071334;
        const BETA_18 = 0.5000585397376548;
        const T8 = 1; // Steering axle constant
        const X8 = 2; // Tandem axle constant
        const Z8 = 3; // Tridem axle constant

        // Calculate dynamic axle loads based on effective total load and percentages
        const dynamicAxleLoads = ESALFormulas.calculateDynamicAxleLoads(effectiveTotalLoad, {
            steeringAxles: config.steeringAxles,
            singleAxles: config.singleAxles,
            tandemAxles: config.tandemAxles,
            tridemAxles: config.tridemAxles,
            steeringLoadPercentage: aircraft.steeringLoadPercentage,
            singleLoadPercentage: aircraft.singleLoadPercentage,
            tandemLoadPercentage: aircraft.tandemLoadPercentage,
            tridemLoadPercentage: aircraft.tridemLoadPercentage,
        });

        // Convert loads to kips for EALF calculations (dynamicAxleLoads already per-axle)
        const steeringLoadPerAxleKips = dynamicAxleLoads.steeringAxleLoad * KN_TO_KIPS;
        const singleLoadKips = dynamicAxleLoads.singleAxleLoad * KN_TO_KIPS;
        const tandemLoadKips = dynamicAxleLoads.tandemAxleLoad * KN_TO_KIPS;
        const tridemLoadKips = dynamicAxleLoads.tridemAxleLoad * KN_TO_KIPS;

        // Calculate individual EALF values for internal calculations (not displayed)
        let ealfSteeringAxle = 0;
        let ealfSingleAxle = 0;
        let ealfTandemAxle = 0;
        let ealfTridemAxle = 0;

        // Steering EALF calculation
        if (steeringLoadPerAxleKips > 0 && config.steeringAxles > 0) {
            const betaX = 0.4 + (0.081 * Math.pow(steeringLoadPerAxleKips + T8, 3.23)) / (Math.pow(structuralNumber + 1, 5.19) * Math.pow(T8, 3.23));
            ealfSteeringAxle = 1 / (Math.pow(19 / (steeringLoadPerAxleKips + T8), 4.79) * (Math.pow(10, GT / betaX) / Math.pow(10, GT / BETA_18)) * Math.pow(T8, 4.33));
        }

        // Single axle EALF calculation (use correct AASHTO formula)
        if (singleLoadKips > 0 && config.singleAxles > 0) {
            const V8 = 1; // Single axle constant (same as steering)
            const betaX = 0.4 + (0.081 * Math.pow(singleLoadKips + V8, 3.23)) / (Math.pow(structuralNumber + 1, 5.19) * Math.pow(V8, 3.23));
            ealfSingleAxle = 1 / (Math.pow(19 / (singleLoadKips + V8), 4.79) * (Math.pow(10, GT / betaX) / Math.pow(10, GT / BETA_18)) * Math.pow(V8, 4.33));
        }

        // Tandem EALF calculation
        if (tandemLoadKips > 0 && config.tandemAxles > 0) {
            const betaX = 0.4 + (0.081 * Math.pow(tandemLoadKips + X8, 3.23)) / (Math.pow(structuralNumber + 1, 5.19) * Math.pow(X8, 3.23));
            ealfTandemAxle = 1 / (Math.pow(19 / (tandemLoadKips + X8), 4.79) * (Math.pow(10, GT / betaX) / Math.pow(10, GT / BETA_18)) * Math.pow(X8, 4.33));
        }

        // Tridem EALF calculation
        if (tridemLoadKips > 0 && config.tridemAxles > 0) {
            const betaX = 0.4 + (0.081 * Math.pow(tridemLoadKips + Z8, 3.23)) / (Math.pow(structuralNumber + 1, 5.19) * Math.pow(Z8, 3.23));
            ealfTridemAxle = 1 / (Math.pow(19 / (tridemLoadKips + Z8), 4.79) * (Math.pow(10, GT / betaX) / Math.pow(10, GT / BETA_18)) * Math.pow(Z8, 4.33));
        }

        // Calculate equivalent EALF per vehicle (sum of all EALFs)
        const equivalentEalfPerVehicle = ealfSteeringAxle + ealfSingleAxle + ealfTandemAxle + ealfTridemAxle;

        // Calculate ESAL per runway separately
        const esalRunway1 = (aircraft.runway1Vehicles || 0) * equivalentEalfPerVehicle;
        const esalRunway2 = (aircraft.runway2Vehicles || 0) * equivalentEalfPerVehicle;

        // Calculate total ESAL per day (sum of both runways)
        const esalPerDay = esalRunway1 + esalRunway2;

        // Validation logic (from FAA Excel)
        const actualAxleCount = config.steeringAxles + config.singleAxles + config.tandemAxles + config.tridemAxles;
        const axleCountValid = actualAxleCount === config.axles;

        const totalLoadPercentage = aircraft.steeringLoadPercentage + aircraft.singleLoadPercentage + aircraft.tandemLoadPercentage + aircraft.tridemLoadPercentage;
        const loadPercentagesValid = Math.abs(totalLoadPercentage - 100) < 0.01; // Allow small floating point errors

        return {
            ...aircraft,
            effectiveTotalLoad,
            steeringAxleLoad: dynamicAxleLoads.steeringAxleLoad,
            singleAxleLoad: dynamicAxleLoads.singleAxleLoad,
            tandemAxleLoad: dynamicAxleLoads.tandemAxleLoad,
            tridemAxleLoad: dynamicAxleLoads.tridemAxleLoad,
            ealfSteeringAxle,
            ealfSingleAxle,
            ealfTandemAxle,
            ealfTridemAxle,
            equivalentEalfPerVehicle,
            esalRunway1,
            esalRunway2,
            esalPerDay,
            passagePercentage: 100,
            axleCountValid,
            loadPercentagesValid,
        };
    }, []);

    /**
     * Initialize aircraft data with default configurations and Excel values
     */
    const initializeAircraftData = useCallback((): AircraftData[] => {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        return DEFAULT_AIRCRAFT_CONFIGURATIONS.map((config: any) => {
            // Find corresponding default values from Excel data
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            const defaultValues = DEFAULT_AIRCRAFT_VALUES.find((value: any) => value.id === config.id);

            const usersTotalLoad = defaultValues?.usersTotalLoad ?? 0;
            const designTotalLoad = config.designTotalLoad;

            // FAA Excel pattern: E = IF(C14>0,C14,C13) - use user's load if >0, else design load
            const effectiveTotalLoad = usersTotalLoad > 0 ? usersTotalLoad : designTotalLoad;

            const initialAircraft: AircraftData = {
                id: config.id,
                selected: defaultValues?.selected ?? false,
                name: config.name,
                axles: config.axles,
                runway1Vehicles: defaultValues?.runway1Vehicles ?? 0,
                runway2Vehicles: defaultValues?.runway2Vehicles ?? 0,
                usersTotalLoad,
                designTotalLoad,
                effectiveTotalLoad,
                steeringAxles: config.steeringAxles,
                singleAxles: config.singleAxles,
                tandemAxles: config.tandemAxles,
                tridemAxles: config.tridemAxles,
                steeringLoadPercentage: defaultValues?.steeringLoadPercentage ?? config.steeringLoadPercentage,
                singleLoadPercentage: defaultValues?.singleLoadPercentage ?? config.singleLoadPercentage,
                tandemLoadPercentage: defaultValues?.tandemLoadPercentage ?? config.tandemLoadPercentage,
                tridemLoadPercentage: defaultValues?.tridemLoadPercentage ?? config.tridemLoadPercentage,
                steeringAxleLoad: config.steeringAxleLoad,
                singleAxleLoad: config.singleAxleLoad,
                tandemAxleLoad: config.tandemAxleLoad,
                tridemAxleLoad: config.tridemAxleLoad,
                // Initialize new EALF fields to 0 (will be calculated)
                ealfSteeringAxle: 0,
                ealfSingleAxle: 0,
                ealfTandemAxle: 0,
                ealfTridemAxle: 0,
                equivalentEalfPerVehicle: 0,
                esalRunway1: 0,
                esalRunway2: 0,
                esalPerDay: 0,
                passagePercentage: defaultValues?.passagePercentage ?? 0,
                imageSrc: config.imageSrc,
                axleCountValid: true, // Will be calculated
                loadPercentagesValid: true, // Will be calculated
            };

            // Calculate initial ESAL if aircraft is selected and has movements
            return calculateAircraftESAL(initialAircraft);
        });
    }, [calculateAircraftESAL]);

    /**
     * Initialize summary calculation data using Redux summaryInputs for key fields
     */
    const summaryData: SummaryCalculationData = useMemo(() => {
        const totalPassages = aircraft.reduce((sum, aircraft) => {
            if (aircraft.selected) {
                // Sum movements from both runways
                const totalMovements = (aircraft.runway1Vehicles || 0) + (aircraft.runway2Vehicles || 0);
                return sum + totalMovements;
            }
            return sum;
        }, 0);

        // Use Redux summaryInputs for these fields
        const directionalDistribution = summaryInputs.dd;
        const laneDistribution = summaryInputs.dl;
        const analysisPeriod = summaryInputs.analysisPeriod;
        const yearGrowthRate = summaryInputs.growthRate;

        // Calculate compound traffic growth factor using Redux values
        const compoundTrafficGrowthFactor = ESALFormulas.calculateTrafficGrowthFactor(yearGrowthRate, analysisPeriod);

        const totalVolumeTraffic = totalPassages * analysisPeriod * 365;
        const trafficDesignLane = totalVolumeTraffic * directionalDistribution * laneDistribution;

        return {
            totalPassages,
            directionalDistribution,
            laneDistribution,
            analysisPeriod,
            yearGrowthRate,
            compoundTrafficGrowthFactor,
            totalVolumeTraffic,
            trafficDesignLane,
            userDefinedTrafficDesignLane: 0,
        };
    }, [aircraft, summaryInputs]);

    /**
     * Returns the initial summary inputs with correct number types (not literals)
     */
    const getInitialSummaryInputs = useCallback(
        (): CalculationsSummaryInputsAircraft => ({
            dd: Number(ESAL_CONSTANTS.DEFAULT_DD),
            dl: Number(ESAL_CONSTANTS.DEFAULT_DL),
            analysisPeriod: Number(ESAL_CONSTANTS.DEFAULT_ANALYSIS_PERIOD),
            growthRate: Number(ESAL_CONSTANTS.DEFAULT_GROWTH_RATE),
            trafficDesignLane: 0,
        }),
        []
    );

    /**
     * Calculate total ESAL from all selected aircraft
     */
    const totalESAL = useMemo(() => {
        const total = aircraft
            .filter(aircraft => aircraft.selected)
            .reduce((acc, aircraft) => {
                console.log(`Total ESAL calculation - Aircraft ${aircraft.id}: selected=${aircraft.selected}, esalPerDay=${aircraft.esalPerDay}`);
                return acc + aircraft.esalPerDay;
            }, 0);
        console.log(`Total ESAL: ${total}`);
        return total;
    }, [aircraft]);

    // Update aircraft ref when aircraft changes
    useEffect(() => {
        aircraftRef.current = aircraft;
    }, [aircraft]);

    /**
     * Initialize aircraft data when modal opens
     */
    useEffect(() => {
        // Initialize ONLY on first mount after modal open, if data is empty
        if (open && !hasInitialized.current && aircraft.length === 0) {
            dispatch(setAircraft(initializeAircraftData()));
            dispatch(setSummaryInputsAircraft(getInitialSummaryInputs()));
            hasInitialized.current = true;
        }
        if (!open) {
            hasInitialized.current = false;
        }
    }, [open, aircraft.length, dispatch, initializeAircraftData, getInitialSummaryInputs]);

    /**
     * Handle aircraft data updates from grid components
     */
    const handleAircraftUpdate = useCallback(
        (updatedAircraft: AircraftData) => {
            dispatch(updateAircraft(updatedAircraft));
        },
        [dispatch]
    );

    /**
     * Handle Run calculation
     */
    const handleRun = useCallback(async () => {
        setIsCalculating(true);

        try {
            // Simulate calculation delay for UX
            await new Promise(resolve => setTimeout(resolve, 1000));

            // Calculate FAA ESAL CALCULATION (J118) - this is what should be returned
            // J118 = FAA simplified formula: (1 + (r * Y / 200)) * N1 * Y
            const faaEsalCalculation = ESALFormulas.calculateFAAProjectedESAL(totalESAL, summaryInputs.analysisPeriod, summaryInputs.growthRate);

            // Return the FAA ESAL CALCULATION value (48,735,036 for default values)
            onCalculationComplete(Math.round(faaEsalCalculation));
            onClose();
        } catch (error) {
        } finally {
            setIsCalculating(false);
        }
    }, [summaryInputs.growthRate, summaryInputs.analysisPeriod, totalESAL, onCalculationComplete, onClose]);

    /**
     * Handle modal close with cleanup
     */
    const handleClose = useCallback(() => {
        setIsCalculating(false);
        resetInvalidDecimals();
        onClose();
    }, [onClose, resetInvalidDecimals]);

    /**
     * Check if calculation can be run (at least one aircraft selected)
     */
    const canRunCalculation = useMemo(() => {
        return aircraft.some(aircraft => aircraft.selected && ((aircraft.runway1Vehicles || 0) > 0 || (aircraft.runway2Vehicles || 0) > 0));
    }, [aircraft]);
    const canRun = canRunCalculation && !isCalculating && !hasInvalidDecimals;

    // Wrapper to avoid type errors with setState
    const handleSummaryInputsChange = useCallback(
        (inputs: CalculationsSummaryInputsAircraft) => {
            dispatch(setSummaryInputsAircraft(inputs));
        },
        [dispatch]
    );

    /**
     * Handle reset to default values
     */
    const handleReset = useCallback(() => {
        dispatch(resetEsalAircraftState());
        dispatch(setAircraft(initializeAircraftData()));
        dispatch(setSummaryInputsAircraft(getInitialSummaryInputs()));
        resetInvalidDecimals();
    }, [dispatch, initializeAircraftData, getInitialSummaryInputs, resetInvalidDecimals]);

    return (
        <Dialog
            open={open}
            title={t('esal_automatic_calculation_title_airport', 'Automatic Aircraft ESAL Calculation') ?? 'Automatic Aircraft ESAL Calculation'}
            onClose={handleClose}
            className="esal-calculation-modal-airport"
            actions={[
                {
                    text: t('cancel_button_label', 'Cancel'),
                    callback: handleClose,
                    color: 'secondary',
                },
                {
                    text: t('reset_button_label', 'Reset'),
                    callback: handleReset,
                    color: 'warning',
                },
                {
                    text: t('run_button_label', 'Run'),
                    callback: canRun ? handleRun : undefined,
                    disabled: !canRun,
                    color: 'primary',
                },
            ]}>
            <Box
                sx={{
                    p: 2,
                    minHeight: 800,
                    width: '100%', // Occupies all available width
                    '.esal-calculation-modal-airport.all-purpose-dialog .MuiDialog-paper': {
                        maxWidth: '98vw !important',
                        width: '2500px !important',
                        margin: 'auto',
                    },
                }}>
                {/* Main content layout: Table, Reference tables, Summary bottom */}
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, height: 'calc(100% - 80px)' }}>
                    {/* Top panel: Aircraft data grid */}
                    <Box sx={{ flex: '1 1 auto', overflow: 'hidden' }}>
                        <AircraftDataGrid
                            aircraft={aircraft}
                            onAircraftUpdate={handleAircraftUpdate}
                            isEditable={!isCalculating}
                            projectData={projectData}
                            onValidityChange={onDecimalValidityChange}
                            resetKey={decimalResetKey}
                        />
                    </Box>

                    {/* Middle panel: Reference tables for DL and Analysis Period */}
                    <Box sx={{ flexShrink: 0 }}>
                        <ReferenceTablesInfo />
                    </Box>

                    {/* Bottom panel: Calculations summary */}
                    <Box sx={{ flexShrink: 0, borderTop: 1, borderColor: 'divider', pt: 2 }}>
                        <CalculationsSummaryAircraft
                            summaryData={summaryData}
                            totalESAL={totalESAL}
                            isCalculating={isCalculating}
                            onSummaryInputsChange={handleSummaryInputsChange}
                            onValidityChange={onDecimalValidityChange}
                            resetKey={decimalResetKey}
                        />
                    </Box>
                </Box>

                {/* Loading overlay */}
                {isCalculating && (
                    <Box
                        sx={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            right: 0,
                            bottom: 0,
                            backgroundColor: 'rgba(255, 255, 255, 0.8)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            zIndex: 1000,
                        }}>
                        <Box sx={{ textAlign: 'center' }}>
                            <CircularProgress />
                            <Typography variant="body2" sx={{ mt: 1 }}>
                                {t('esal_calculating_message_airport', 'Calculating Aircraft ESAL values...')}
                            </Typography>
                        </Box>
                    </Box>
                )}
            </Box>
        </Dialog>
    );
};

export default EsalCalculationModalAirport;
